/**
 * De stappen van het scherm Code in de taal van de app. GL216 punt 2.
 *
 * Gevonden bij GL206-2 (25 oktober 2026) en toen bewust niet aangeraakt: de agentlus
 * (`backend/services/agent_loop.py`) schrijft haar stappen in het Nederlands, en die
 * stonden zo ook op een Engels scherm. De backend BLIJFT Nederlands schrijven, want zo
 * staan de stappen in het werkruimtelog en in het spoor. Hier wordt bij het TONEN
 * vertaald, zoals `taal-en.js` dat doet voor de vaste teksten van het scherm.
 *
 * Een zin die hier niet staat, blijft staan zoals hij kwam: liever Nederlands dan leeg.
 * `codestappen-taal.test.js` eist dat elke titel die de lus kan schrijven een vorm heeft.
 */

const VAST = {
  "Gestopt: je drukte op Stop": "Stopped: you pressed Stop",
  "Gestopt: de noodstop staat aan": "Stopped: the emergency stop is on",
  "Gestopt: het tijdsbudget van de app-opdracht is op": "Stopped: the time budget of the app task is used up",
  "Gebruikt wat deze werkruimte eerder leerde": "Used what this workspace learned before",
  "Het model kon niet antwoorden": "The model could not answer",
  "Het model gaf geen opdracht terug die de agent kan uitvoeren":
    "The model returned no instruction the agent can carry out",
  "Gestopt: het resultaat past nog steeds niet bij de taak": "Stopped: the result still does not fit the task",
  Klaar: "Done",
  "Heeft jou nodig": "Needs you",
  "Niet klaar: er is niets gedaan": "Not done: nothing was done",
  "Niet klaar: de laatste uitvoering mislukte": "Not done: the last run failed",
  "Kon de uitkomst niet controleren": "Could not check the outcome",
  "Gecontroleerd: het resultaat beantwoordt de taak": "Checked: the result answers the task",
  "Het resultaat beantwoordt de taak niet": "The result does not answer the task",
  Geleerd: "Learned",
  "Een bestand uitgevoerd": "A file ran",
  "Netwerk geweigerd": "Network refused",
  "Netwerk bereikt": "Network reached",
  "Gestopt: de tijdslimiet is verstreken": "Stopped: the time limit ran out",
  "Pakketten installeren toestaan": "Allow installing packages",
  // GL222-2: de vaste weigeringen van de pakketinstallatie (services/pakketten.py, `scherm`).
  "Het netwerk van deze werkruimte staat niet aan voor pakketten, dus er is niets geinstalleerd. Kies rechtsboven in deze werkruimte 'Netwerk voor pakketten' en start de agent opnieuw.":
    "The network of this workspace is not on for packages, so nothing was installed. Choose 'Network for packages' at the top right of this workspace and start the agent again.",
  "Geen Python op deze machine heeft een werkende pip, dus er kan niets worden geinstalleerd.":
    "No Python on this machine has a working pip, so nothing can be installed.",
  "npm staat niet op deze machine, dus er kunnen geen npm-pakketten worden geinstalleerd.":
    "npm is not on this machine, so no npm packages can be installed.",
  "Node staat niet op deze machine, dus er kunnen geen npm-pakketten worden geinstalleerd.":
    "Node is not on this machine, so no npm packages can be installed.",
  "Noem minstens een pakket.": "Name at least one package.",
  // De afloopzinnen (`run-steps.js` OUTCOMES).
  "Klaar, en het resultaat past bij de taak": "Done, and the result fits the task",
  "Klaar, maar het resultaat is niet gecontroleerd": "Done, but the result was not checked",
  "Het resultaat paste niet bij de taak": "The result did not fit the task",
  "Wacht op jou": "Waiting for you",
  "Gestopt: de agent bleef zichzelf herhalen": "Stopped: the agent kept repeating itself",
  "Gestopt: de rondes zijn op": "Stopped: the rounds ran out",
  "Gestopt: de toetsen bleven rood": "Stopped: the tests stayed red",
  "Gestopt: de opdracht vroeg om een toets en die is er niet": "Stopped: the task asked for a test and there is none",
  "Nog niet klaar: de opdracht vraagt om een toets": "Not finished yet: the task asks for a test",
  "Gestopt zonder resultaat": "Stopped without a result",
  "Vastgelopen: een gereedschap liep stuk": "Stuck: a tool broke",
  "Het model gaf in geen enkele ronde een opdracht terug die de agent kan uitvoeren. Probeer de taak anders te zeggen, of kies een ander model.":
    "In no round did the model return an instruction the agent can carry out. Try saying the task differently, or choose another model.",
  // De labels per soort stap (`run-steps.js` AGENT_TITLES).
  Taak: "Task",
  Vastgelopen: "Stuck",
  Ronde: "Round",
  Onthouden: "Remembered",
  "Bestand geschreven": "File written",
  "Bestand gelezen": "File read",
  Uitgevoerd: "Ran",
  "Uitkomst gecontroleerd": "Outcome checked",
  Probleem: "Problem",
  "Draait in kringetjes": "Going round in circles",
  "Verliet deze machine": "Left this machine",
  Gestopt: "Stopped",
  // LC3 (31 okt 2026).
  Plan: "Plan",
  Toetsen: "Tests",
  // LC4.
  "Gezocht in de code": "Searched the code",
  // CA1.
  "Bestandenlijst gelezen": "File list read",
  "Verwijderd": "Deleted",
  Verplaatst: "Moved",
  "Plan voor deze rit": "Plan for this run",
  "Geen plan: het model gaf er geen": "No plan: the model gave none",
  "Kon de toetsen niet draaien": "Could not run the tests",
  // Glasronde 0.9.232: de weigering van het toetsgereedschap en de afsluiting na een toetsrit.
  "Alleen een toetsbestand test_*.py bovenin deze werkruimte draait als toets":
    "Only a test file named test_*.py at the top of this workspace runs as tests",
  "De toetsen zijn groen en het resultaat is bevestigd.": "The tests are green and the result is confirmed.",
  "De bestaande toetsen bleven groen en het resultaat is bevestigd.":
    "The existing tests stayed green and the result is confirmed.",
  "Kon niet zoeken in de code": "Could not search the code",
  // CA2 (31 okt 2026).
  "Installeren kan niet": "Cannot install",
  "Installatie wacht op jou": "Installation waiting for you",
  // GL223-2
  Geinstalleerd: "Installed",
  "Installatie geweigerd": "Installation refused",
  "Al geïnstalleerd": "Already installed",
  // CA8 (1 nov 2026): de agent maakt zelf een issue.
  Issue: "Issue",
  "Geen issue gemaakt": "No issue created",
  "Geen toetsen gevonden: maak een bestand test_*.py met unittest": "No tests found: make a file test_*.py with unittest",
  // Glasronde 0.9.229 fout 8: de tellers van de lus, in de vormen van spoor_taal (agent_loop.teller_nl en waarom_nl).
  "Dit is dezelfde stap als eerder en hij mislukte opnieuw. Herhaal hem niet. Kies een andere aanpak: lees het bestand, probeer iets eenvoudigers, of stel een vraag.":
    "This is the same step as before and it failed again. Do not repeat it. Change approach: read the file, try something simpler, or ask a question.",
  "Dat gaf hetzelfde antwoord als de vorige keer, dus er veranderde niets. Stel een andere vraag in plaats van dezelfde opnieuw.":
    "That gave the same answer as last time, so nothing changed. Ask a different question rather than the same one again.",
  // Glasronde 0.9.229 fout 5: een deelstap schrijft alleen zijn eigen bestanden.
  "hoort bij een andere deelstap": "belongs to another sub-step",
};

/** Zinnen met een pad, een getal of een naam erin. VOLGORDE IS EEN EIS: de algemene
 *  "Gestopt: ..." staat achteraan, anders slokt hij de bijzondere op. */
// Glasronde 0.9.229 fout 8: de tellers van de lus (backend spoor_taal.TELLERS).
const TELLERS_EN = { "mislukte aanroep": "failed call", "hetzelfde gereedschap": "same tool", "hetzelfde resultaat": "same result" };

const PATRONEN = [
  // GL222-2: de kop van een uitvoering (run_reading.py `kop`) en het aantal regels van een schrijfstap.
  [/^(.+) is klaar\.$/, "$1 finished."],
  [/^(.+) is gestopt: de tijdslimiet is verstreken\.$/, "$1 was stopped: it ran past the time limit."],
  [/^(.+) kreeg geen netwerk: (.+)\.$/, "$1 was refused the network: $2."],
  [/^(.+) stopte met exitcode (-?\d+) en zei niet waarom\.$/, "$1 exited $2 and said nothing about why."],
  [/^(.+) stopte met exitcode (-?\d+): (.+)$/, "$1 exited $2: $3"],
  [/^(.+) faalde op regel (\d+): (.+)$/, "$1 failed on line $2: $3"],
  [/^(.+) faalde: (.+)$/, "$1 failed: $2"],
  [/^(\d+) regels$/, "$1 lines"],
  [/^Ronde (\d+) van (\d+): het model wordt gevraagd$/, "Round $1 of $2: asking the model"],
  [/^Vastgelopen: het gereedschap (.+) liep stuk$/, "Stuck: the tool $1 broke"],
  [/^Gestopt: (\d+) rondes zonder resultaat$/, "Stopped: $1 rounds without a result"],
  [/^Gestopt: de toetsen bleven rood na (\d+) pogingen$/, "Stopped: the tests stayed red after $1 attempts"],
  // Glasronde 0.9.232: met de bestanden die draaiden, en wat ze niet raken. De specifieke vormen eerst.
  [/^Toetsen groen: (\d+) geslaagd in (.+); die raken (.+) niet$/, "Tests green: $1 passed in $2; they do not test $3"],
  [/^Toetsen groen: (\d+) geslaagd in (.+)$/, "Tests green: $1 passed in $2"],
  [/^Toetsen groen: (\d+) geslaagd$/, "Tests green: $1 passed"],
  [/^Issue #(\S+) gemaakt: (.+)$/, "Issue #$1 created: $2"],
  [/^(pip|npm): (.+)\. Komt mee: (.+)$/, (m, man, pk, mee) => `${man}: ${pk}. Comes along: ${mee === "niets" ? "nothing" : mee}`],
  [/^Gezocht in de code: (.+) \((.+) treffers\)$/, "Searched the code: $1 ($2 matches)"],
  [/^Toetsen rood \((\d+) gedraaid\)$/, "Tests red ($1 ran)"],
  [/^Geen toetsen geteld met (\w+): de uitvoer gaf geen telling$/, "No tests counted with $1: the output gave no count"],
  [/^Draait in kringetjes: (mislukte aanroep|hetzelfde gereedschap|hetzelfde resultaat)$/,
    (m, teller) => `Going round in circles: ${TELLERS_EN[teller]}`],
  [/^Gestopt: (mislukte aanroep|hetzelfde gereedschap|hetzelfde resultaat)$/, (m, teller) => `Stopped: ${TELLERS_EN[teller]}`],
  [/^Dat zijn (\d+) aanroepen van (.+) achter elkaar\. Zoek eerst uit wat er misgaat, en blijf gereedschap gebruiken in plaats van over te stappen op tekst\.$/,
    "That is $1 calls to $2 in a row. Find out first what goes wrong, and keep using tools rather than switching to text."],
  [/^Draait in kringetjes: (.+)$/, "Going round in circles: $1"],
  [/^Schrijven geweigerd: (.+)$/, "Writing refused: $1"],
  [/^Kon (.+) niet schrijven$/, "Could not write $1"],
  [/^Kon (.+) niet lezen$/, "Could not read $1"],
  [/^Kon (.+) niet uitvoeren$/, "Could not run $1"],
  // CA1 (31 okt 2026): bewerken, verplaatsen en verwijderen.
  [/^Kon (.+) niet aanpassen$/, "Could not edit $1"],
  [/^Kon (.+) niet verplaatsen$/, "Could not move $1"],
  [/^Kon (.+) niet verwijderen$/, "Could not delete $1"],
  [/^Verwijderen geweigerd: (.+)$/, "Deleting refused: $1"],
  [/^(.+) verplaatst naar (.+)$/, "$1 moved to $2"],
  [/^(.+) aangepast$/, "$1 edited"],
  [/^(.+) verwijderd$/, "$1 deleted"],
  [/^(.+) bevat een syntaxfout$/, "$1 has a syntax error"],
  [/^(.+) geschreven$/, "$1 written"],
  [/^(.+) gelezen$/, "$1 read"],
  [/^(.+) uitgevoerd$/, "$1 ran"],
  // GL223-1: het detail onder "<bestand> uitgevoerd".
  [/^met (python|bash)$/, "with $1"],
  [/^Onbekend gereedschap (.+)$/, "Unknown tool $1"],
  // Een leeg antwoord bij een denkstand; de lus vraagt een stand lager (agent_loop.vraag_rol).
  [/^Geen antwoord bij denkstand (.+); opnieuw met (.+)$/,
    (m, van, naar) => `No answer at thinking ${DENKSTANDEN[van] || van}; again with ${DENKSTANDEN[naar] || naar}`],
  // Glasronde 0.9.231 punt 3: het detail eronder, met de tijd die het kostte.
  [/^Het model dacht (\d+) s zonder antwoord$/, "The model thought for $1 s without an answer"],
  [/^Laatste probleem: (.+)$/, (m, titel) => `Last problem: ${stapInTaal(titel, "en")}`],
  [/^Gestopt: (.+)$/, "Stopped: $1"],
];

/** De tekst van een stap in `taal`. Nederlands blijft Nederlands. PUUR. */
// LC5 (31 okt 2026): de rol en denkstand van een ronde of het plan (agent_loop.rol_regel).
const ROLNAMEN = { planner: "planner", uitvoerder: "executor", toetser: "tester" };
const DENKSTANDEN = { laag: "low", gemiddeld: "medium", hoog: "high" };
for (const [rol, role] of Object.entries(ROLNAMEN)) {
  VAST[`Rol: ${rol}, dit model kent geen denkstand`] = `Role: ${role}, this model has no thinking level`;
  for (const [stand, level] of Object.entries(DENKSTANDEN)) {
    VAST[`Rol: ${rol}, denkstand ${stand}`] = `Role: ${role}, thinking ${level}`;
  }
}

export function stapInTaal(tekst, taal) {
  if (taal !== "en" || !tekst) return tekst;
  if (Object.prototype.hasOwnProperty.call(VAST, tekst)) return VAST[tekst];
  for (const [patroon, vorm] of PATRONEN) {
    if (patroon.test(tekst)) return tekst.replace(patroon, vorm);
  }
  return tekst;
}

/** Een stapdetail per regel in `taal` (LC5: de rolregel staat boven het plan). PUUR. */
export function detailInTaal(tekst, taal) {
  if (taal !== "en" || !tekst) return tekst;
  return tekst.split("\n").map((regel) => stapInTaal(regel, taal)).join("\n");
}

/** Na hoeveel rondes, in `taal`. PUUR. */
export function naRondes(rondes, taal) {
  if (taal === "en") return `after ${rondes} ${rondes === 1 ? "round" : "rounds"}`;
  return `na ${rondes} ${rondes === 1 ? "ronde" : "rondes"}`;
}
